import Cookies from 'js-cookie';
import { getQueryParams } from 'src/utils/QueryStringUtil';
import * as GATES from './gates';

/**x
 * Tuple representing a serialized gates payload.
 * - index 0: version string (e.g. "v1")
 * - index 1: array of serialized gate entries
 *
 * Each serialized gate entry has the form: `${name}^${id}^${value}`
 * where `id` is either a remote identifier or a sentinel `TEST` for local overrides/placeholders.
 */
export type Gates = [string, string[]];

/**
 * Storage keys and sentinel ID used by the debug utilities.
 *
 * - `FEATURE_FLAG_COOKIE` is the cookie/localStorage key used by the remote snapshot (Statsig/AB reader).
 *   The payload follows: `${version}~${gate1|gate2|...}`.
 * - `FEATURE_FLAG_OVERRIDES_KEY` is a dedicated localStorage key we use to persist developer overrides only.
 *   This separation prevents seeded placeholders (which also use the TEST sentinel id) from being
 *   confused with explicit user overrides.
 * - `FAKE_ID` is the sentinel id we use for both seeded placeholders and for persisted local overrides.
 */
export const FEATURE_FLAG_COOKIE = 'expFlagCtx';
export const FEATURE_FLAG_OVERRIDES_KEY = 'expFlagCtxOverrides';
export const FAKE_ID = 'TEST';

/**
 * Persist developer overrides into the dedicated overrides localStorage key.
 * The value MUST be a serialized payload produced by `serialize(version, gates)`.
 * We intentionally do NOT inspect/transform the value here; callers should ensure it contains
 * only override entries (typically entries with `FAKE_ID`).
 */
export const setOverrides = (value: string) => {
  localStorage.setItem(FEATURE_FLAG_OVERRIDES_KEY, value);
};

/**
 * Read persisted developer overrides from the dedicated overrides key and return the
 * list of serialized entries (each entry is `name^id^value`). Returns an empty array
 * if the key is absent or malformed.
 */
export const readOverrides = (): string[] => {
  try {
    const des = deserialize(localStorage.getItem(FEATURE_FLAG_OVERRIDES_KEY));
    return des ? des[1] : [];
  } catch {
    return [];
  }
};

/**
 * Parse a single serialized entry of the form `name^id^value` and return the boolean value
 * if it represents a boolean string "true"/"false", otherwise return undefined.
 */
export const parseSerializedEntryValue = (entry: string | null | undefined): boolean | undefined => {
  if (!entry) {
    return undefined;
  }
  const parts = entry.split('^');
  if (parts.length < 3) {
    return undefined;
  }
  const v = parts[2];
  if (v === 'true') {
    return true;
  }
  if (v === 'false') {
    return false;
  }
  return undefined;
};

// ---------- Shared snapshot helpers (DRY) ---------------------------------------------------------------

/**
 * Read the remote snapshot (prefer cookie, fall back to legacy localStorage copy).
 * Returns a Gates tuple (version, entries[]). Always returns a valid tuple.
 */
const readRemoteSnapshot = (): Gates => {
  const cookieSnapshot = deserialize(Cookies.get(FEATURE_FLAG_COOKIE));
  const legacyLocalRemote = deserialize(localStorage.getItem(FEATURE_FLAG_COOKIE));
  return cookieSnapshot ?? legacyLocalRemote ?? ['v1', []];
};

/**
 * Read the persisted overrides snapshot. If absent, returns an empty list using the provided
 * fallback version (usually the remote snapshot version).
 */
const readOverridesSnapshot = (fallbackVersion: string): Gates => {
  return deserialize(localStorage.getItem(FEATURE_FLAG_OVERRIDES_KEY)) ?? [fallbackVersion, []];
};

/**
 * Merge serialized entries into a mutable map of name -> {id, value}.
 * Entries later in the array overwrite earlier ones.
 */
const mergeEntriesIntoMap = (
  map: Record<string, { id: string; value: string }>,
  entries: string[],
  definedGateNames: string[]
) => {
  for (let i = 0; i < entries.length; i++) {
    const raw = entries[i];
    if (raw == null) {
      continue;
    }
    const parsed = parseGateEntry(raw);
    if (!parsed) {
      continue;
    }
    if (definedGateNames.includes(parsed.name) || parsed.id === FAKE_ID) {
      map[parsed.name] = { id: parsed.id, value: parsed.value };
    }
  }
};

// The AB reader only assumes 1 version so we will as well
export const serialize = (version: string, gates: string[]): string => `${version}~${gates.join('|')}`;

/**
 * Parsed gate entry helper type.
 * Keep `value` as string to avoid accidental coercion differences (values may be "true"/"false" or other tokens).
 */
interface ParsedGateEntry {
  name: string;
  id: string;
  value: string;
}

/**
 * Parse a single gate entry of the form "name^id^value". Returns null if malformed.
 */
const parseGateEntry = (entry: string): ParsedGateEntry | null => {
  if (!entry) {
    return null;
  }
  const parts = entry.split('^');
  if (parts.length !== 3) {
    return null;
  }
  const name = parts[0] ?? '';
  const id = parts[1] ?? '';
  const value = parts[2] ?? '';
  if (!name) {
    return null;
  }
  return { name, id, value };
};

/**
 * Deserialize a serialized gates payload stored in cookie or localStorage.
 * Expected format: `${version}~${gate1|gate2|...}`.
 * This function performs minimal validation and returns `null` for malformed input.
 */
export const deserialize = (raw: string | null | undefined): Gates | null => {
  if (!raw) {
    return null;
  }
  // Find the separator between version and gates. Avoid splitting the full string unnecessarily.
  const tildIndex = raw.indexOf('~');
  if (tildIndex <= 0 || tildIndex === raw.length - 1) {
    return null; // missing version or empty gates segment
  }
  const version = raw.slice(0, tildIndex);
  const gatesSegment = raw.slice(tildIndex + 1);
  if (!version || !gatesSegment) {
    return null;
  }
  // Return array of non-empty entries. Consumers will further validate each entry format.
  return [version, gatesSegment.split('|').filter(Boolean)];
};

/**
 * Build a unified gate list with precedence layers:
 * 1. Placeholder (ensures all defined gates appear even if absent elsewhere)
 * 2. localStorage snapshot
 * 3. Cookie snapshot (wins over localStorage for duplicate gates)
 * 4. Query param forced overrides (value override only, keep existing id)
 *
 * We store final entries as `${name}^${id}^${value}`. If an override (query param) is applied
 * we reuse the existing id to preserve remote provenance unless it is a placeholder (FAKE_ID).
 */
export const loadFeatureGates = (): Gates => {
  // Remote baseline: cookie first (authoritative), fall back to local copy if any legacy persisted under cookie key in localStorage
  const remoteSnapshot: Gates = readRemoteSnapshot();
  const overridesSnapshot = readOverridesSnapshot(remoteSnapshot[0]);

  // Gate definitions imported from ../gates.ts. We expect an object of named exports where
  // the values are gate names; using Object.values() preserves the definition order.
  const definedGateNames = Object.values(GATES) as string[];

  // Map of gateName -> { id, value }
  const map: Record<string, { id: string; value: string }> = {};

  // 1. Placeholders: ensure every defined gate appears in the final list so the debug UI
  //    shows a consistent row for each gate even if it doesn't appear in snapshots/overrides.
  for (let i = 0; i < definedGateNames.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const n = definedGateNames[i]!; // non-null assertion: Object.values(GATES) returns strings
    map[n] = { id: FAKE_ID, value: 'false' };
  }

  // Merge remote snapshot then overrides into our map (overrides win)
  mergeEntriesIntoMap(map, remoteSnapshot[1], definedGateNames);
  mergeEntriesIntoMap(map, overridesSnapshot[1], definedGateNames);

  // 4. Query param overrides: only change value, keep existing id for provenance
  const queryParams = getQueryParams();
  for (const key in queryParams) {
    if (!definedGateNames.includes(key)) {
      continue;
    }
    const overrideValue = String(queryParams[key]);
    const existing = map[key];
    if (existing) {
      map[key] = { id: existing.id, value: overrideValue };
    }
  }

  // Build stable list in the same order as the defined gates. We seeded placeholders in
  // `definedGateNames` order and mutate them in-place above, so iterating `definedGateNames`
  // produces a stable and predictable ordering for the UI.
  const finalEntries: string[] = [];
  for (let i = 0; i < definedGateNames.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const name = definedGateNames[i]!; // assert non-null
    const g = map[name];
    if (!g) {
      continue; // should not happen
    }
    finalEntries.push(`${name}^${g.id}^${g.value}`);
  }

  return [remoteSnapshot[0], finalEntries];
};

// --------------------------------------------------------------------------------------------------------------------
// Gate source computation
// --------------------------------------------------------------------------------------------------------------------

export type GateSource = 'QUERY_PARAM' | 'OVERRIDE' | 'REMOTE' | 'PLACEHOLDER';

/**
 * Determine the provenance (source) of each gate's current value based on precedence rules.
 * Precedence (highest -> lowest): Query Param (QUERY_PARAM) > Override (local TEST) > Remote (cookie/local) > Placeholder
 */
export const computeGateSources = (): Record<string, GateSource> => {
  const sources: Record<string, GateSource> = {};
  const definedGateNames = new Set<string>(Object.values(GATES) as string[]);

  // Initialize all as PLACEHOLDER first
  definedGateNames.forEach(n => {
    sources[n] = 'PLACEHOLDER';
  });

  const remoteSnapshot = readRemoteSnapshot();
  const overridesSnapshot = readOverridesSnapshot(remoteSnapshot[0]);

  // Mark remote - only mark as REMOTE where currently PLACEHOLDER
  for (let i = 0; i < remoteSnapshot[1].length; i++) {
    const raw = remoteSnapshot[1][i];
    if (raw == null) {
      continue;
    }
    const parsed = parseGateEntry(raw);
    if (!parsed) {
      continue;
    }
    if (!definedGateNames.has(parsed.name)) {
      continue;
    }
    if (sources[parsed.name] === 'PLACEHOLDER') {
      sources[parsed.name] = 'REMOTE';
    }
  }

  // Mark overrides
  for (let i = 0; i < overridesSnapshot[1].length; i++) {
    const raw = overridesSnapshot[1][i];
    if (raw == null) {
      continue;
    }
    const parsed = parseGateEntry(raw);
    if (!parsed) {
      continue;
    }
    if (parsed.id === FAKE_ID && definedGateNames.has(parsed.name)) {
      sources[parsed.name] = 'OVERRIDE';
    }
  }

  // Query params (highest precedence)
  const queryParams = getQueryParams();
  for (const key in queryParams) {
    if (definedGateNames.has(key)) {
      sources[key] = 'QUERY_PARAM';
    }
  }

  return sources;
};
